import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, Link } from "react-router-dom";

import TermsAndConditionsModal from "./components/TermsAndConditionsModal";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DeleteAccount from "./pages/DeleteAccount";
import ReferralRedirectPage from "./pages/ReferralRedirectPage";
import ProductDeepLink from "./pages/ProductDeepLink";
import Leto from "./pages/leto/Leto";
import YoutubePlayer from "./pages/YoutubePlayer";

import "./App.css";

import {
  getAuth,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from "firebase/auth";

import { initializeApp, getApps, getApp } from "firebase/app";
import { getFirestore, doc, getDoc } from "firebase/firestore";

/* ---------------- FIREBASE ---------------- */

const firebaseConfig = {
  apiKey: "AIzaSyDyGVPHnTVyWimYSzCpGmMF_RMm1gxhpW4",
  authDomain: "pitics.com",
  projectId: "piticsjgat",
  storageBucket: "piticsjgat.firebasestorage.app",
  messagingSenderId: "898712575285",
  appId: "1:898712575285:web:cc5d6b753ae1597b38807c"
};

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: "select_account" });

/* ---------------- SIDEBARS ---------------- */

const LeftSidebar = () => (
  <aside className="sidebar left-sidebar">
    <div className="side-panel side-panel-blue"><span className="side-eyebrow">THE PITICS EXPERIENCE</span><h2>Ideas into things people love.</h2><p>Discover thoughtful products, personal touches and creative possibilities in one place.</p></div>
    <div className="side-panel side-panel-light"><span className="side-icon">✦</span><h3>Made to feel personal</h3><p>Explore products that help make everyday moments memorable.</p></div>
  </aside>
);
const RightSidebar = () => (
  <aside className="sidebar right-sidebar">
    <div className="side-panel side-panel-warm"><span className="side-eyebrow">GROW WITH PITICS</span><h2>Your business deserves a bigger stage.</h2><p>Bring your products to more customers with a simple seller journey.</p><Link className="side-cta" to="/seller/login">Explore Seller Portal <span>→</span></Link></div>
    <div className="side-panel side-panel-dark"><h3>Built for creators and businesses</h3><p>List products, manage orders and build your presence with PITICS.</p></div>
  </aside>
);

/* ---------------- PRO CHECK ---------------- */

const checkProStatus = async (uid) => {
  const docRef = doc(db, "proUsers", uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data().isPro || false;
  }

  return false;
};

/* ---------------- APP ---------------- */

const PBuyer = () => {
  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isAccountMenuOpen, setAccountMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  const dropdownRef = useRef(null);

  /* -------- RELOAD -------- */
  useEffect(() => {
    const hasReloaded = sessionStorage.getItem("hasReloaded");
    if (!hasReloaded) {
      sessionStorage.setItem("hasReloaded", "true");
      window.location.reload();
    }
  }, []);

  /* -------- RESIZE -------- */
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 1024) setMobileNavOpen(false);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* -------- AUTH -------- */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        try {
          const proStatus = await checkProStatus(currentUser.uid);
          setIsPro(proStatus);
        } catch (error) {
          console.error("Error checking pro status:", error);
          setIsPro(false);
        }
      } else {
        setIsPro(false);
      }
    });

    return () => unsubscribe();
  }, []);

  /* -------- OUTSIDE CLICK -------- */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };

    if (isAccountMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isAccountMenuOpen]);

  /* -------- AUTH ACTIONS -------- */

  const handleSignIn = async () => {
    try {
      if (/Mobi|Android/i.test(navigator.userAgent)) {
        await signInWithRedirect(auth, provider);
      } else {
        await signInWithPopup(auth, provider);
      }
    } catch (error) {
      console.error("Sign-in error", error);
      alert("Sign-in failed. Please try again.");
    }
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);

    try {
      await signOut(auth);
      setUser(null);
      setIsPro(false);
      setAccountMenuOpen(false);
    } catch (error) {
      console.error("Sign-out error", error);
      alert("Sign-out failed. Please try again.");
    } finally {
      setIsSigningOut(false);
      setShowSignOutConfirm(false);
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="app">

      {/* HEADER */}
      <header className="header">
        <div className="header-content">

          {windowWidth <= 1024 && (
            <button
              className="mobile-nav-toggle"
              onClick={() => setMobileNavOpen(!isMobileNavOpen)}
            >
              ☰
            </button>
          )}

          <img
            src="/banner.png"
            alt="Banner"
            className={windowWidth > 1024 ? "banner-image" : "banner-image1"}
          />

          {/* DESKTOP NAV */}
          {windowWidth > 1024 && (
            <nav className="desktop-nav desktop-nav-inline">
              <ul className="nav-links">
                <li><Link className="nav-home" to="/">Home</Link></li>
                <li><Link to="/seller/login">Seller</Link></li>
                <li><a href="https://play.google.com/store/apps/details?id=com.liyagroup.pitics" target="_blank" rel="noopener noreferrer">Download</a></li>
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </nav>
          )}

          {/* AUTH */}
          <div className="auth-section">
            {user ? (
              <div className="account-menu">
                <button
                  onClick={() => setAccountMenuOpen(!isAccountMenuOpen)}
                  className="account-button"
                >
                  <img
                    src={user.photoURL || "/jashvin.png"}
                    alt=""
                    className="profile-image"
                  />
                </button>

                <div
                  className={`account-dropdown ${isAccountMenuOpen ? "active" : ""}`}
                  ref={dropdownRef}
                >
                  <button
                    className="close-btn"
                    onClick={() => setAccountMenuOpen(false)}
                  >
                    ✖
                  </button>

                  <p>Welcome, {user.displayName || "User"}</p>
                  {isPro && <p className="pro-badge">🌟 Pro Active</p>}
                  <button onClick={() => setShowSignOutConfirm(true)}>
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={handleSignIn} className="gsi-material-button">
                <div className="gsi-material-button-content-wrapper">
                  <div className="gsi-material-button-icon">
                    <img
                      src="/glogo.png"
                      alt="Google Logo"
                      style={{ width: "24px", height: "24px" }}
                    />
                  </div>
                  <span style={{ display: "none" }}>Sign in with Google</span>
                </div>
              </button>
            )}
          </div>

        </div>
      </header>

      {/* MOBILE NAV */}
      {windowWidth <= 1024 && isMobileNavOpen && (
        <nav className="mobile-nav">
          <button className="close-nav" onClick={() => setMobileNavOpen(false)}>
            ✖
          </button>

          <ul className="nav-links">
            <li><Link to="/" onClick={() => setMobileNavOpen(false)}>Home</Link></li>
            <li><Link to="/seller/login" onClick={() => setMobileNavOpen(false)}>Seller</Link></li>
            <li><a href="https://play.google.com/store/apps/details?id=com.liyagroup.pitics" target="_blank" rel="noopener noreferrer" onClick={() => setMobileNavOpen(false)}>Download</a></li>
            <li><Link to="/contact" onClick={() => setMobileNavOpen(false)}>Contact</Link></li>
          </ul>
        </nav>
      )}

      {/* MAIN */}
      <main className="main-content">
        {windowWidth > 1024 && <LeftSidebar />}

        <div className="center-content">
          <Routes>

            <Route path="/" element={
              <div className="pitics-home">
                <section className="home-hero">
                  <div className="hero-copy">
                    <span className="hero-kicker"><span className="kicker-dot" /> A CREATIVE MARKETPLACE FOR EVERYDAY LIFE</span>
                    <h1>Make it personal.<br /><em>Make it PITICS.</em></h1>
                    <p className="hero-lead">PITICS brings people, products and creativity together. Discover thoughtful finds, explore personalized experiences and connect with sellers who turn ideas into products worth sharing.</p>
                    <div className="hero-actions"><a className="primary-action" href="https://play.google.com/store/apps/details?id=com.liyagroup.pitics" target="_blank" rel="noopener noreferrer">Explore the PITICS App <span>↗</span></a><Link className="secondary-action" to="/seller/login">Start selling <span>→</span></Link></div>
                    <div className="hero-proof"><span>✦</span> Discover · Create · Share · Grow</div>
                  </div>
                  <div className="hero-art" aria-label="PITICS creative marketplace"><div className="art-orbit orbit-one" /><div className="art-orbit orbit-two" /><div className="art-card art-card-main"><div className="art-logo">P</div><span className="art-label">YOUR IDEAS,</span><strong>YOUR WORLD.</strong><small>Welcome to PITICS</small></div><div className="art-float art-float-top">✦ Create something special</div><div className="art-float art-float-bottom"><span>↗</span> Made to discover</div><div className="art-spark spark-one">✳</div><div className="art-spark spark-two">✦</div></div>
                </section>
                <section className="home-intro"><div className="section-heading"><span className="section-kicker">ONE PLATFORM. MORE POSSIBILITIES.</span><h2>A better way to discover, create and grow.</h2><p>Whether you are shopping for something meaningful or building a business of your own, PITICS is designed to make every step feel simple, inspiring and rewarding.</p></div>
                  <div className="feature-grid"><article className="feature-card feature-purple"><div className="feature-icon">✧</div><h3>Discover products</h3><p>Explore a growing range of products and find something that fits your style, needs and special moments.</p><span className="feature-number">01</span></article><article className="feature-card feature-orange"><div className="feature-icon">♡</div><h3>Make it meaningful</h3><p>Bring a personal touch to everyday essentials and gifts with creative product experiences.</p><span className="feature-number">02</span></article><article className="feature-card feature-green"><div className="feature-icon">↗</div><h3>Support sellers</h3><p>Connect with independent sellers and businesses offering products made with care.</p><span className="feature-number">03</span></article></div>
                </section>
                <section className="seller-banner"><div className="seller-banner-orb" /><div className="seller-banner-copy"><span className="section-kicker">YOUR NEXT CHAPTER STARTS HERE</span><h2>Have products to share with the world?</h2><p>Turn your passion into opportunity. Join PITICS as a seller, showcase your products and take your business to customers looking for something special.</p><ul className="seller-benefits"><li>✓ Create your seller profile</li><li>✓ List and manage your products</li><li>✓ Track orders and grow your business</li></ul><Link className="seller-action" to="/seller/login">Become a PITICS Seller <span>→</span></Link></div><div className="seller-visual"><div className="seller-visual-ring"><div className="seller-visual-inner"><span>✦</span><strong>GROW</strong><small>with PITICS</small></div></div><div className="seller-tag tag-one">Your store, your story</div><div className="seller-tag tag-two">Start something great ↗</div></div></section>
                <section className="download-section" id="download-app"><div className="download-logo-wrap"><img src="/logo.png" alt="PITICS app logo" /></div><div className="download-copy"><span className="section-kicker">TAKE PITICS WITH YOU</span><h2>Good things are just a tap away.</h2><p>Download the PITICS app to explore products and enjoy the experience wherever you go.</p></div><a className="play-store-action" href="https://play.google.com/store/apps/details?id=com.liyagroup.pitics" target="_blank" rel="noopener noreferrer"><span className="play-triangle">▶</span><span><small>GET IT ON</small><strong>Google Play</strong></span><span className="play-arrow">↗</span></a></section>
              </div>
            } />

            <Route path="/notebook-labels" element={<Leto />} />

            <Route path="/ref/:code" element={<ReferralRedirectPage />} />
            <Route path="/p/:productId" element={<ProductDeepLink />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/contact" element={<TermsAndConditionsModal />} />
            <Route path="/youtube-player" element={<YoutubePlayer />} />

          </Routes>
        </div>

        {windowWidth > 1024 && <RightSidebar />}
      </main>

      {/* FOOTER 
      <div className="blinking-banner">
        PITICS - Personal Informative Text Included For Communication On Stickers
      </div>
*/}
      <footer className="footer"><div className="footer-inner"><div className="footer-brand"><img src="/logo.png" alt="PITICS" /><div><strong>PITICS</strong><span>Discover. Create. Grow.</span></div></div><div className="footer-links"><Link to="/">Home</Link><Link to="/seller/login">Seller</Link><a href="https://play.google.com/store/apps/details?id=com.liyagroup.pitics" target="_blank" rel="noopener noreferrer">Download</a><Link to="/contact">Contact</Link><Link to="/privacy-policy">Privacy</Link></div><p className="footer-copyright">Pitics Technologies <sup>&copy;</sup> 2024–2026. All rights reserved.</p></div></footer>

      {/* SIGN OUT */}
      {showSignOutConfirm && (
        <div className="signout-modal">
          <div className="modal-content">
            <p>Are you sure you want to sign out?</p>

            <button onClick={handleSignOut} disabled={isSigningOut}>
              {isSigningOut ? "Signing out..." : "Yes, Sign Out"}
            </button>

            <button onClick={() => setShowSignOutConfirm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default PBuyer;